import dayjs from 'dayjs'
import { merge, sortBy, uniqBy, sum, compact, omit } from 'es-toolkit'
import { floor, max, min } from 'es-toolkit/compat'
import { useMemo, useCallback, useEffect, useState } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import { COLOR_TYPES } from 'api/constants'
import type { DailyWork } from 'api/reports/types'
import { CONNECTION_TYPES } from 'api/schedule_types/constants'

import { compactAverage } from 'slices/reportsSlice'
import { getScheduleTypeList, selectScheduleTypesStatus } from 'slices/scheduleTypesSlice'

import { colorTypeToCode } from 'components/Dashboard/utils'
import { BadgeButton, Chart, Table } from 'components/common'
import type { BadgeItem, Series, TableHeaderType, TableCellType } from 'components/common/types'
import { createLineChartOptions } from 'components/common/utils'

import { createXAxis } from './utils'

import styles from './ProductivityGraph.module.scss'

type ProductivityBadgeItem = BadgeItem & {
  unit: string
}

type DailyWorkArrayRow = {
  date: string
  dailyAvgPlanCount: Array<number | null>
  dailyAvgRecordCount: Array<number | null>
  dailyPlanTime: Array<number | null>
  dailyRecordSum: Array<number | null>
  dailyPlanSum: Array<number | null>
  dailyAvgProductivity: Array<number | null>
}

type SummaryDailyRow = {
  date: string
  dailyAvgPlanCount: number | null
  maxProductivity: number | null
  minProductivity: number | null
  planTime: number | null
  recordCount: number | null
  planCount: number | null
}

type GraphCustomData = {
  date: string
  planTimeData: number | null
  recordCountData: number | null
}

type Props = {
  dailyWorkData: DailyWork[]
  start: Date
  end: Date
  useIndividual?: boolean
}

export const ProductivityGraph = ({ dailyWorkData, start, end, useIndividual = true }: Props) => {
  const { workspaceId } = useParams<'workspaceId'>()
  const dispatch = useDispatch()
  const [selectedBadgeKeys, setSelectedBadgeKeys] = useState<number[]>([])

  const { partialScheduleTypes } = useSelector(selectScheduleTypesStatus, shallowEqual)

  useEffect(() => {
    if (!workspaceId) {
      return
    }
    dispatch(getScheduleTypeList(Number(workspaceId)))
  }, [dispatch, workspaceId])

  const badgeItems = useMemo(() => {
    const formattedDailyWorkData = dailyWorkData.map<ProductivityBadgeItem>(dailyWork => ({
      color: dailyWork.scheduleTypeColor,
      key: dailyWork.scheduleTypeId,
      label: dailyWork.scheduleTypeName,
      unit: dailyWork.unit,
    }))
    const dailyWorkDataUniqueByKey = uniqBy(formattedDailyWorkData, data => data.key)

    const targetType = useIndividual ? CONNECTION_TYPES.AUTO : CONNECTION_TYPES.MANUAL
    const scheduleTypeIds = partialScheduleTypes.filter(s => s.connectionType === targetType).map(s => s.id)

    return sortBy(dailyWorkDataUniqueByKey, ['key']).filter(i => scheduleTypeIds.includes(i.key))
  }, [dailyWorkData, useIndividual, partialScheduleTypes])

  useEffect(() => {
    // badgeItemsが更新されたら全選択
    setSelectedBadgeKeys(badgeItems.map(badgeItem => badgeItem.key))
  }, [badgeItems])

  const calcAvgProductivity = useCallback((value: number | null, planTime: number | null) => {
    return planTime && planTime > 0 && value ? value / planTime : 0
  }, [])

  const createSummaryDailyRows = useCallback(
    (badgeKey: number) => {
      const dailyWorkRows = dailyWorkData
        .filter(dailyWork => dailyWork.scheduleTypeId === badgeKey)
        .flatMap(dailyWork => dailyWork.data)
      const summaryArrayDailyRows = dailyWorkRows.reduce((acc: DailyWorkArrayRow[], cur) => {
        const target = acc.find(row => row.date === cur.date)
        if (!target) {
          return acc.concat([
            {
              date: cur.date,
              dailyAvgPlanCount: [cur.dailyAvgPlanCount],
              dailyAvgRecordCount: [cur.dailyAvgRecordCount],
              dailyPlanTime: [cur.planTime],
              dailyRecordSum: [cur.recordCount],
              dailyPlanSum: [cur.planCount],
              dailyAvgProductivity: [calcAvgProductivity(cur.recordCount, cur.planTime)],
            },
          ])
        }

        target.dailyAvgPlanCount.push(cur.dailyAvgPlanCount)
        target.dailyAvgRecordCount.push(cur.dailyAvgRecordCount)
        target.dailyPlanTime.push(cur.planTime)
        target.dailyRecordSum.push(cur.recordCount)
        target.dailyPlanSum.push(cur.planCount)
        target.dailyAvgProductivity.push(calcAvgProductivity(cur.recordCount, cur.planTime))
        return acc
      }, [])

      // compactAverage で null を除いた平均に置き換え、sum(compact())でnullを除いた合計に置き換える
      return summaryArrayDailyRows.map((row): SummaryDailyRow => {
        return {
          date: row.date,
          dailyAvgPlanCount: compactAverage(row.dailyAvgPlanCount),
          maxProductivity: max(row.dailyAvgProductivity) ?? 0,
          minProductivity: min(row.dailyAvgProductivity) ?? 0,
          planTime: row.dailyPlanTime.length === 0 ? 0 : sum(compact(row.dailyPlanTime)),
          recordCount: row.dailyRecordSum.length === 0 ? 0 : sum(compact(row.dailyRecordSum)),
          planCount: row.dailyPlanSum.length === 0 ? 0 : sum(compact(row.dailyPlanSum)),
        }
      })
    },
    [calcAvgProductivity, dailyWorkData]
  )

  const summaryRowData = useMemo(() => {
    return badgeItems.map(item => {
      return {
        ...item,
        data: createSummaryDailyRows(item.key),
      }
    })
  }, [badgeItems, createSummaryDailyRows])

  const scheduleTypeSummaryList = useMemo(() => {
    return summaryRowData.map(row => {
      const dailyMaxProductivity = row.data.map(r => r.maxProductivity)
      const dailyMinProductivity = row.data.map(r => r.minProductivity)
      const planTimeSum = sum(compact(row.data.map(r => r.planTime)))
      const recordSum = sum(compact(row.data.map(r => r.recordCount)))

      return {
        key: row.key,
        color: row.color,
        label: row.label,
        unit: row.unit,
        avgProductivity: planTimeSum === 0 ? 0 : recordSum / planTimeSum,
        planTimeSum: planTimeSum,
        recordSum: recordSum,
        maxProductivity: floor(max(dailyMaxProductivity) || 0, 1).toFixed(1),
        minProductivity: floor(min(dailyMinProductivity) || 0, 1).toFixed(1),
      }
    })
  }, [summaryRowData])

  const chartOptions = useMemo(() => {
    const selectedBadges = badgeItems.filter(badge => selectedBadgeKeys.includes(badge.key))
    const xAxisData = createXAxis(start, end)

    const yAxis: Series[] = []
    selectedBadges.forEach(graphBadge => {
      const colorCode = colorTypeToCode(graphBadge.color)
      const summaryRows = createSummaryDailyRows(graphBadge.key)

      const planData = xAxisData.map(date => {
        // パフォーマンス改善のために日付を直接文字列として比較する
        const target = summaryRows.find(row => row.date === date)

        const planTime = target?.planTime ?? null
        const planCount = target?.planCount ?? null
        const recordData = calcAvgProductivity(planCount, planTime)

        return target?.dailyAvgPlanCount ? recordData : null
      })
      yAxis.push({
        type: 'line',
        color: colorCode,
        data: planData,
        name: graphBadge.label,
        custom: {
          unit: graphBadge.unit ?? '',
        },
      })

      const recordGraphData = xAxisData.map(date => {
        // パフォーマンス改善のために日付を直接文字列として比較する
        const target = summaryRows.find(row => row.date === date)

        const planTime = target?.planTime ?? null
        const recordCount = target?.recordCount ?? null
        const recordData = calcAvgProductivity(recordCount, planTime)

        return {
          date: date,
          recordData: target?.dailyAvgPlanCount ? recordData : null,
          planTimeData: target ? target.planTime : null,
          recordCountData: target ? target.recordCount : null,
        }
      })

      yAxis.push({
        type: 'area',
        color: colorCode,
        data: recordGraphData.map(record => record.recordData),
        name: graphBadge.label,
        custom: {
          unit: graphBadge.unit ?? '',
          customData: recordGraphData.map(item => omit(item, ['recordData'])),
        },
      })
    })

    const options = createLineChartOptions(
      {
        xAxis: {
          data: xAxisData,
        },
        yAxis,
      },
      'Dash',
      'day'
    )
    merge<Highcharts.Options, Highcharts.Options>(options, {
      tooltip: {
        formatter() {
          const isPlan = this.series.userOptions.type === 'line'
          const planText = isPlan ? '（予測）' : ''
          const tooltipTextData = [
            dayjs(this.x).format('YYYY/MM/DD'),
            this.series.name,
            `${floor(this.y || 0, 1).toFixed(1)}${this.series.options.custom!.unit}/1時間${planText}`,
          ]
          if (!isPlan) {
            const target = this.series.options.custom!.customData.find((data: GraphCustomData) => data.date === this.x)
            const planTime = target ? target.planTimeData : '-'
            const recordCount = target ? target.recordCountData : '-'

            tooltipTextData.push(`作業実績合計:${recordCount}${this.series.options.custom!.unit}`)
            tooltipTextData.push(`投入人時合計:${planTime}人時`)
          }
          return '<div style="text-align:center">' + tooltipTextData.join('<br>') + '</div>'
        },
      },
      xAxis: {
        tickInterval: Math.ceil(xAxisData.length / 7),
      },
    })
    return options
  }, [badgeItems, start, end, selectedBadgeKeys, createSummaryDailyRows, calcAvgProductivity])

  const tableHeaders: TableHeaderType[] = [
    { value: '作業名', width: '25%', className: 'border-end' },
    { value: '平均生産性', width: '15%' },
    { value: '作業実績合計', width: '15%' },
    { value: '投入人時合計', width: '15%' },
    { value: '最高生産性', width: '15%' },
    { value: '最低生産性', width: '15%' },
  ]

  const tableItems: TableCellType[][] = useMemo(
    () =>
      scheduleTypeSummaryList.map(data => [
        {
          value: (
            <div className="d-flex align-items-center px-3">
              <div className={`${styles.square} bg-${data.color} me-1`} />
              <div className="text-truncate">{data.label}</div>
            </div>
          ),
          className: 'border-end',
        },
        { value: `${floor(data.avgProductivity, 1).toFixed(1)} ${data.unit}/1時間` },
        { value: `${data.recordSum} ${data.unit}` },
        { value: `${floor(data.planTimeSum, 2)} 人時` },
        { value: `${data.maxProductivity} ${data.unit}/1時間` },
        { value: `${data.minProductivity} ${data.unit}/1時間` },
      ]),
    [scheduleTypeSummaryList]
  )

  return (
    <div>
      <Chart options={chartOptions} />
      <div className="d-flex row mx-2 mt-4">
        <BadgeButton
          items={badgeItems}
          selected={selectedBadgeKeys}
          onChange={selected => setSelectedBadgeKeys(selected)}
        />
      </div>
      <hr color={COLOR_TYPES.SILVER} />
      <div className="mb-3">
        <div className="fw-bold mb-2">選択対象の指定期間のデータ</div>
        <Table header={tableHeaders} body={tableItems} />
      </div>
    </div>
  )
}
